import React, { useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { Helmet } from "react-helmet";
import { LicenseInfo } from "@mui/x-license-pro";
import LoadingScreen from "components/LoadingElements/LoadingScreen";
import { parse } from "query-string";
import { theme } from "styles/themes/index";
import { useEffectOnce } from "react-use";
import * as locales from "@mui/material/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import RootBoundary from "components/ErrorBoundaries/RootBoundary";
import SettingsClient from "util/SettingsClient";
import SessionClient from "util/SessionClient";
import ApiClient from "util/ApiClient";
import { store, browserHistory } from "rdx/configureStore";
import { registerSessionFactory } from "rdx/services/api";
import RootRouter from "containers/router";
import { ThemeModeProvider } from "context/ThemeModeContext";
import initI18n from "./i18n";
import { configureColorVars } from "./styles/colorVariables";

import "./styles/global.css";

registerSessionFactory(() => new SessionClient());

const Start = () => {
  const api = new ApiClient();
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [i18nInitialized, setI18nInitialized] = useState(false);

  const [locale, setLocale] = useState("enUS");
  const themeWithLocale = useMemo(() => createTheme(theme, locales[locale]), [locale]);
  const session = new SessionClient();
  const userLocale = session?.user?.props?.locale;

  useEffect(() => {
    const systemLocales = {
      en: "enUS",
      es: "esES",
    };
    setLocale(systemLocales[userLocale] || "enUS");
  }, [userLocale]);

  LicenseInfo.setLicenseKey(
    "74a034dcd380e2d230a76c32a782acceTz0xMjMzNzcsRT0xNzk5NzExOTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
  );

  useEffectOnce(() => {
    (async () => {
      /* Conditionally import ResizeObserver polyfill to prevent consultant dashboard
       * crashing on Safari versions 11-14. In the future, if we need to add more
       * conditional polyfills like this, may look into custom polyfill bundling
       * via polyfill.io to keep this init hook clean.
       */
      if ("ResizeObserver" in window === false) {
        const module = await import("@juggle/resize-observer");
        window.ResizeObserver = module.ResizeObserver;
      }
    })();

    configureColorVars();

    api.get("/settings", {}).then((res) => {
      new SettingsClient().setSettings(res);
      setSettingsLoaded(true);
    });

    if (session.user) {
      session.user.reload("boot").finally(() => {
        if (session.user?.props?.locale) {
          localStorage.setItem("i18nextLng", session.user.props.locale);
        }
        setSessionLoaded(true);
      });
    } else {
      setSessionLoaded(true);
    }

    const TEN_MINUTES = 1000 * 60 * 10;
    setInterval(() => {
      if (session.user) {
        session.user.reload("interval-10m", { no401redirect: false });
      }
    }, TEN_MINUTES); // refresh jwt every 10 minutes for notification and inbox counts updates.

    if (session.validSession && api.isSso()) {
      api.ssoLogin();
    }

    initI18n().then(() => {
      setI18nInitialized(true);
    });
  });

  const ssoForm = () => (
    <form action={ApiClient.defaults.host + ApiClient.defaults.urls.sso} method="POST" id="sso_form">
      <input type="hidden" name="Authorization" value={session.jwt} id="sso_jwt" />
      <input type="hidden" name="RelayState" value={parse(window.location.search).ReturnUrl} id="sso_relay_state" />
      <input type="hidden" name="route" value={parse(window.location.search).sso} id="sso_route" />
    </form>
  );

  if (!settingsLoaded || !sessionLoaded || !i18nInitialized) {
    return (
      <>
        <LoadingScreen />
        {ssoForm()}
      </>
    );
  }

  const description = "Powur: Accelerating the adoption of sustainable energy worldwide.";

  return (
    <ThemeProvider theme={themeWithLocale}>
      <ThemeModeProvider>
        <Provider store={store}>
          {ssoForm()}
          <Router history={browserHistory}>
            <RootBoundary>
              <Helmet>
                <meta name="description" content={description} />
                <meta name="og:description" content={description} />
              </Helmet>
              <RootRouter />
            </RootBoundary>
          </Router>
        </Provider>
      </ThemeModeProvider>
    </ThemeProvider>
  );
};

export default Start;
